import { DeleteButton } from '@profgeosoft-ui/react';
import clsx from 'clsx';
import { observer } from 'mobx-react-lite';
import type { MouseEvent } from 'react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ConfirmDeleteGeoTaskModal } from 'src/features/modals/confirm-delete-geo-task-modal';
import PlusInBox from 'src/shared/assets/icons/plus-in-box.svg';
import { Button } from 'src/shared/components/button';
import { SwitchButtonGroup } from 'src/shared/components/switch-button-group';
import type { TPromiseController } from 'src/shared/utils/promise-controller';
import { createPromiseController } from 'src/shared/utils/promise-controller';
import type { WellFormManagerWithGeoTasksHistory } from 'src/store/well-form-manager/well-form-manager-with-geo-tasks-history';

import styles from './well-form-geo-tasks-list.module.scss';

type Props = {
  wellFormManager: WellFormManagerWithGeoTasksHistory;
};

type TGeoTaskTitleProps = {
  index: number;
  isDeleteAvailable: boolean;
  deleteGeoTask: VoidFunction;
};

const GeoTaskTitle = observer<TGeoTaskTitleProps>(function GeoTaskTitle({ index, isDeleteAvailable, deleteGeoTask }) {
  const { t } = useTranslation();

  const onDelete = (e: MouseEvent) => {
    e.stopPropagation();
    deleteGeoTask();
  };

  return (
    <div className={styles.geoTaskTitle}>
      <p>{t('newWellForm:GeaoTasks.geotask', { index })}</p>
      {isDeleteAvailable && <DeleteButton onClick={onDelete} />}
    </div>
  );
});

export const GeoTasksList = observer(function GeoTasksList({ wellFormManager }: Props) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [promiseController, setPromiseController] = useState<TPromiseController<boolean> | null>(null);

  const handleChange = (id: number) => {
    wellFormManager.changeForm(id);
  };

  const onApply = () => {
    promiseController?.resolve(true);
    setIsModalOpen(false);
  };

  const onCancel = () => {
    promiseController?.resolve(false);
    setIsModalOpen(false);
  };

  const onDeleteGeoTask = useCallback(
    async (wellFormId: number): Promise<void> => {
      setIsModalOpen(true);

      const promiseController = createPromiseController<boolean>();
      setPromiseController(promiseController);
      const shouldDelete = await promiseController;

      if (shouldDelete) {
        wellFormManager.deleteGeoTask(wellFormId);
      }

      setPromiseController(null);
    },
    [wellFormManager],
  );

  return (
    <>
      <div className={clsx(styles.wrapper)}>
        <SwitchButtonGroup
          value={wellFormManager.currentFormStore?.id}
          variant="underlined"
          defaultOptionKey={wellFormManager.formList[0]?.id ?? 0}
          items={wellFormManager.formList.map((wellForm) => {
            const isDeleteAvailable =
              wellForm === wellFormManager.formList.at(-1) && wellFormManager.formList.length > 1;

            return {
              key: wellForm.id,
              title: (
                <GeoTaskTitle
                  index={wellForm.index + 1}
                  isDeleteAvailable={isDeleteAvailable}
                  deleteGeoTask={() => onDeleteGeoTask(wellForm.id)}
                />
              ),
              value: wellForm.id,
            };
          })}
          onChange={handleChange}
        />
        <Button
          variant="tertiary"
          width={24}
          height={24}
          icon={<PlusInBox />}
          onClick={wellFormManager.copyGeologicalTask}
        />
      </div>
      <ConfirmDeleteGeoTaskModal isModalOpened={isModalOpen} onApply={onApply} onCancel={onCancel} />
    </>
  );
});
